import { autoinject } from 'aurelia-framework';
import { NavigationInstruction, Next, PipelineStep } from 'aurelia-router';
import { StateHistory, Store } from 'aurelia-store';

import { AppRouteID } from '~/app-routes';
import { AppState } from '~/app-store';
import { NavigationState } from '~/components/navigation/navigation-state';
import { CommonURLParam } from '~/constants';
import { DialogUtils } from '~/utils/dialog-utils';
import { Logger } from '~/utils/logger';
import { RouteState } from '~/utils/route-state';

/**
 * Setup default URL parameters.
 * By using a PipelineStep, we can cancel the previous route when we updated the parameters
 */
@autoinject
export class UrlParameterStep implements PipelineStep {
	private constructor(
		readonly dialogUtils: DialogUtils,
		readonly store: Store<StateHistory<AppState>>,
		private readonly navState: NavigationState,
	) {}

	public run(instruction: NavigationInstruction, next: Next): Promise<any> {
		// NOTE: Jake: 2019-05-15 - WEBUI-1918
		// When a user navigates from another route, auto-close all dialog boxes
		if (this.dialogUtils) {
			this.dialogUtils.closeAll();
		}

		return this.processURLParams(instruction, next);
	}

	private processURLParams(
		navigationInstruction: NavigationInstruction,
		next: Next,
	): Promise<any> {
		// no need to process URL params for login page
		if (navigationInstruction.fragment === '/' + AppRouteID.Login) {
			return next();
		}
		const router = navigationInstruction.router;
		Logger.log('Processing URL params ...');

		// Determine the default values for nav-* params if not provided yet
		const params: { [param: string]: any } =
			UrlParameterStep.calculateDefaultUrlParams(
				this.navState,
				navigationInstruction,
			);

		// currently putting parameters on manual navigation to any page
		if (
			UrlParameterStep.hasUpdate(
				navigationInstruction.queryParams,
				params,
			)
		) {
			// During this step, the constructor of the form will be called but the rest lifecycle
			// steps will be ignored. This will cause fails to clean up side effects set in constructor;
			return next.cancel(
				RouteState.getRedirectForRouteParameters(
					navigationInstruction,
					params,
				),
			);
		}

		return next();
	}

	private static calculateDefaultUrlParams(
		navigationState: NavigationState,
		navigationInstruction: NavigationInstruction,
	): { [param: string]: any } {
		// Determine the default values for nav-view and nav-id if not provided yet
		const params: { [param: string]: any } = {};

		let navView =
			navigationInstruction.queryParams[CommonURLParam.NAV_VIEW];
		let navId = navigationInstruction.queryParams[CommonURLParam.NAV_ID];
		let division =
			navigationInstruction.queryParams[CommonURLParam.DIVISION];

		// Default to first of values based on navigation data
		if (navigationState) {
			if (!navView) {
				navView = navigationState.viewNameList[0];
			}
			if (navView) {
				const navViewStructure = navigationState.structure[navView];
				if (navViewStructure) {
					// nav-id
					if (!navId) {
						navId = navViewStructure.structure.entityList[0].id;
					}

					// divison
					if (!division) {
						division = navViewStructure.structure.division;
					}
				}
			}
		}

		if (navView) {
			params[CommonURLParam.NAV_VIEW] = navView;
		}
		if (navId) {
			params[CommonURLParam.NAV_ID] = navId;
		}
		if (division) {
			params[CommonURLParam.DIVISION] = division;
		}

		return params;
	}

	/**
	 * See if our updated parameters are different than what was already the URL.
	 *
	 * WARNING:
	 * We do *not* want to check if URL parameters were removed for this logic so we aren't reusing
	 * RouteState.hasURLParamsChanged() by design. This is only used in a context wherein we want to apply
	 * default values if they aren't already applied.
	 */
	private static hasUpdate(
		originalParams: { [param: string]: any },
		newParams: { [param: string]: any },
	): boolean {
		return Object.keys(newParams).some(
			(key) => originalParams[key] !== newParams[key],
		);
	}
}
